import React, { useState, useRef, useEffect } from 'react';
import { useQuoteCalculator } from './QuoteCalculator';
import QuotePreview from './QuotePreview';
import './ProjectSummary.css';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useReactToPrint } from 'react-to-print';
import { FiDownload } from 'react-icons/fi';
import Presentation from './Presentation';
import { SENSOR_TYPES, SPACE_TYPES } from '../../utils/constants';
import { usePricing } from '../../contexts/PricingContext';

/**
 * ProjectSummary component for creating project summaries
 * 
 * @param {Object} props - Component props
 * @param {Object} props.sensorCounts - Counts of different sensor types
 * @param {string} props.projectName - Name of the project
 * @param {Function} props.onClose - Function to call when closing the generator
 * @param {boolean} props.isPageMode - Whether the component is being used as a page
 * @param {Object} props.floorplanData - Optional floorplan data containing boxes, shapes, and entryPoints
 * @returns {JSX.Element} ProjectSummary component
 */
const ProjectSummary = ({ sensorCounts: propSensorCounts, projectName: initialProjectName, onClose, isPageMode = false, floorplanData, project }) => {
  const [clientName, setClientName] = useState('');
  const [projectNameValue, setProjectNameValue] = useState(initialProjectName || '');
  const [projectAddress, setProjectAddress] = useState('');
  const [preparerName, setPreparerName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const quoteRef = useRef(null);
  const [calculatedSensorCounts, setCalculatedSensorCounts] = useState(null);
  const [showPresentation, setShowPresentation] = useState(false);
  const fullDocumentRef = useRef(null);
  
  // Retrieve pricing context
  const { pricingData } = usePricing();
  
  // Effect to sync projectName with the loaded project
  useEffect(() => {
    if (project) {
      setProjectNameValue(project.name || '');
    }
  }, [project]);
  
  // Effect to update projectNameValue when initialProjectName changes
  useEffect(() => {
    setProjectNameValue(initialProjectName || '');
  }, [initialProjectName]);
  
  // Calculate sensor counts from floorplan data if available
  useEffect(() => {
    if (floorplanData) {
      const { boxes = [], shapes = [], entryPoints = [] } = floorplanData;
      
      let entry = 0;
      let oa = 0;
      let waffleRoomFlex = 0;
      let waffleDeskPhone = 0;
      
      // Count boxes
      boxes.forEach(box => {
        if (box.type === SENSOR_TYPES.OA) {
          oa += box.count || 1;
        } else if (box.type === SENSOR_TYPES.WAFFLE) {
          // Determine pricing category based on space category
          if (box.spaceCategory === SPACE_TYPES.DESK) {
            waffleDeskPhone += box.count || 1;
          } else if (box.spaceCategory === SPACE_TYPES.ROOM || box.spaceCategory === SPACE_TYPES.FLEX || box.spaceCategory === SPACE_TYPES.PHONE) {
            waffleRoomFlex += box.count || 1;
          } else {
            // Default to desk/phone if not specified (backward compatibility)
            waffleDeskPhone += box.count || 1;
          }
        } else if (box.type === SENSOR_TYPES.ENTRY) {
          entry += box.count || 1;
        }
      });
      
      // Count shapes
      shapes.forEach(shape => {
        if (shape.type === SENSOR_TYPES.OA) {
          oa += shape.count || 1;
        } else if (shape.type === SENSOR_TYPES.WAFFLE) {
          // Determine pricing category based on space category
          if (shape.spaceCategory === SPACE_TYPES.DESK) {
            waffleDeskPhone += shape.count || 1;
          } else if (shape.spaceCategory === SPACE_TYPES.ROOM || shape.spaceCategory === SPACE_TYPES.FLEX || shape.spaceCategory === SPACE_TYPES.PHONE) {
            waffleRoomFlex += shape.count || 1;
          } else {
            // Default to desk/phone if not specified (backward compatibility)
            waffleDeskPhone += shape.count || 1;
          }
        } else if (shape.type === SENSOR_TYPES.ENTRY) {
          entry += shape.count || 1;
        }
      });
      
      // Count entry points
      entryPoints.forEach(entryPoint => {
        if (entryPoint.type === SENSOR_TYPES.ENTRY) {
          entry += entryPoint.count || 1;
        }
      });
      
      setCalculatedSensorCounts({ 
        entry, 
        oa, 
        waffleRoomFlex, 
        waffleDeskPhone 
      });
    }
  }, [floorplanData]);
  
  // Use calculated counts if available, otherwise use prop counts
  const effectiveSensorCounts = calculatedSensorCounts || propSensorCounts;
  
  // For debugging
  console.log('Effective sensor counts:', effectiveSensorCounts);
  
  // Calculate quote details based on sensor counts
  // Pass 0 as discount since discount functionality is commented out
  const quoteDetails = useQuoteCalculator(effectiveSensorCounts, 0);
  
  // For debugging
  console.log('Quote details:', quoteDetails);
  
  // Reset showPresentation to false when in page mode
  useEffect(() => {
    if (isPageMode) {
      setShowPresentation(false);
    }
  }, [isPageMode]);
  
  // Form handlers
  const handleClientNameChange = (e) => {
    setClientName(e.target.value);
  };

  const handleProjectNameChange = (e) => {
    setProjectNameValue(e.target.value);
  };

  const handleProjectAddressChange = (e) => {
    setProjectAddress(e.target.value);
  };

  const handlePreparerNameChange = (e) => {
    setPreparerName(e.target.value);
  };
  
  // Use react-to-print for printing the entire document
  const handlePrintFullDocument = useReactToPrint({
    content: () => fullDocumentRef.current,
    documentTitle: `${projectNameValue || 'Project'} - Complete Presentation`,
    onBeforeGetContent: () => {
      // Show all presentation slides before print
      setShowPresentation(true);
      return Promise.resolve();
    },
    onAfterPrint: () => {
      console.log('Complete presentation PDF created successfully!');
    }
  });
  
  // Generate PDF quote
  const handlePrint = () => {
    const element = quoteRef.current;
    
    if (!element) {
      console.error('Print component not found');
      return;
    }

    // Always use landscape orientation for commercial style
    const orientation = 'landscape';
    
    setIsGenerating(true);
    
    html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
    }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: orientation,
        unit: 'mm',
      });
      
      const imgWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight, '', 'FAST');
      pdf.save(`${projectNameValue || 'Project'} - Summary.pdf`);
      setIsGenerating(false);
    }).catch(error => {
      console.error('Error generating PDF:', error);
      setIsGenerating(false);
    });
  };
  
  // Choose the print method based on whether we need the full presentation
  const handleDownloadPDF = () => {
    if (showPresentation) {
      handlePrintFullDocument();
    } else {
      handlePrint();
    }
  };
  
  // Download as image
  const downloadImage = async () => {
    if (!quoteRef.current) return;
    
    setIsGenerating(true);
    
    try {
      const quoteElement = quoteRef.current;
      const canvas = await html2canvas(quoteElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const link = document.createElement('a');
      link.download = `${projectNameValue.replace(/\s+/g, '_')}_Commercial_Summary.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error generating image:', error);
      alert('There was an error generating the image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };
  
  return (
    <div className={`project-summary ${isPageMode ? 'page-mode' : ''}`} ref={fullDocumentRef}>
      <div className="summary-container">
        <div className="summary-header">
          <h2>Project Summary</h2>
          <div className="summary-actions">
            <button 
              className="download-button pdf"
              onClick={handleDownloadPDF}
              disabled={isGenerating}
            >
              <FiDownload /> {isGenerating ? 'Generating...' : 'Download PDF'}
            </button>
            <button 
              className="download-button image"
              onClick={downloadImage}
              disabled={isGenerating}
            >
              {isGenerating ? 'Generating...' : 'Download Image'}
            </button>
            {!isPageMode && (
              <button className="close-button" onClick={onClose}>
                Close
              </button>
            )}
          </div>
        </div>
      </div>
      
      <div className="preview-container">
        <div className="preview-wrapper" ref={quoteRef}>
          <QuotePreview
            quoteDetails={quoteDetails}
            projectName={projectNameValue}
            style="commercial"
            onTogglePresentation={() => setShowPresentation(!showPresentation)}
            showPresentation={showPresentation}
            isPageMode={isPageMode}
          />
        </div>
        
        {/* Presentation Component - only render when explicitly viewing presentation */}
        {showPresentation && !isPageMode && (
          <Presentation 
            quoteDetails={quoteDetails}
            projectName={projectNameValue}
            quoteStyle="commercial"
            visible={true}
          />
        )}
      </div>
    </div>
  );
};

export default ProjectSummary; 